import React, { useEffect, useMemo } from 'react'
import { makeStyles } from '@mui/styles'
import { useSelector, useDispatch } from 'react-redux'
import isBoolean from 'lodash/isBoolean'
import PropTypes from 'prop-types'

import { setTotalSteps } from '@/redux/ducks/wizard/config'

import OnboardingUser from './onboardingUser'
import OnboardingPassword from './onboardingPassword'
import OnboardingAvvConsent from './onboardingAvvConsent'
import isAdditionalTenant from './helpers/is-additional-tenant'
import OnboardingCompanyAddress from './onboardingCompanyAddress'
import OnboardingAvvAndAgb from './onboardingAvvAndAgb'
import { isMeinBuero } from '@/utils/build-env'
import UserExperienceSelection from './onboardingUserExperience'
import { useElevateHubSpotChat } from '@/modules/hubspot/_helpers/useElevateHubSpotChat'
import GtmService, { GtmEventTypes } from '@/modules/_services/gtm.service'

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    '@media (max-width: 900px)': {
      overflowY: 'auto',
    },
  },
}))

const isMB = isMeinBuero()

const Onboarding = ({ onClose }) => {
  const dispatch = useDispatch()
  const classes = useStyles()

  const hasPasswordDefined = useSelector(state => state?.user?.info?.hasPasswordDefined)
  const subscriptions = useSelector(state => state.tenant.info?.subscriptions)
  const step = useSelector(state => state.wizard.config.step)
  const isTaxConsultant = subscriptions?.some(s => s.isTaxConsultant)
  const tenants = useSelector(state => state?.user?.info?.tenants)
  const activeTenantId = useSelector(state => state?.user?.info?.activeTenant?.tenantId)
  const isFurtherCompany = isAdditionalTenant({ tenantId: activeTenantId, tenants })

  const subscriptionTypeTypesLabel = subscriptions?.map(s => s.typeOfSubscription).join(', ') || ''

  // Use the custom hook to elevate HubSpot chat for mb
  useElevateHubSpotChat()

  const steps = useMemo(() => {
    const taxConsultantSteps = isTaxConsultant
      ? [
        <OnboardingCompanyAddress />,
        <OnboardingAvvAndAgb onClose={onClose} />,
      ]
      : []

    // orgaMAX registration: password (or only the AVV, when a password exists already - e.g. set via
    // "Passwort vergessen" before the registration was finished), then the short question.
    // A further company of an existing user ("Neue Firma erstellen") keeps the old AVV step only.
    const registrationSteps = [
      hasPasswordDefined ? <OnboardingAvvConsent /> : <OnboardingPassword />,
      <UserExperienceSelection onClose={onClose} />,
    ]
    const omoSteps = isTaxConsultant
      ? taxConsultantSteps
      : isFurtherCompany
        ? [<OnboardingAvvAndAgb onClose={onClose} />]
        : registrationSteps

    const mboSteps = [
      <OnboardingUser onClose={onClose} />,
      ...taxConsultantSteps,
    ]

    const steps = (isMB ? mboSteps : omoSteps).filter(Boolean)
    dispatch(setTotalSteps(steps.length - 1))
    return steps
  }, [isTaxConsultant, hasPasswordDefined, isFurtherCompany])

  useEffect(() => {
    if (ENV.product === 'orgamax') {
      const hubspotChat = document.getElementById('hubspot-messages-iframe-container')
      if (hubspotChat) {
        hubspotChat.style.visibility = 'hidden'
      }
      // Defining interval (DO-3376)
      const intervalInstance = setInterval(() => {
        if (isBoolean(window.UC_UI?.isConsentRequired()) && !window.UC_UI?.isConsentRequired()) {
          GtmService.pushEvent({
            event: GtmEventTypes.GaEventHub,
            eventCategory: 'Onboarding',
            eventAction: 'Start',
            eventLabel: `Erster Onboarding-Screen ${subscriptionTypeTypesLabel}`,
          })
          clearInterval(intervalInstance)
        }
      }, 2000)

      return () => {
        clearInterval(intervalInstance)
        if (hubspotChat) {
          hubspotChat.style.visibility = 'visible'
        }
      }
    }
  }, [])

  useEffect(() => {
    setTimeout(() => {
      const scrollToEl = document.querySelector(`.${classes.container}`)
      scrollToEl && scrollToEl.scrollTo(0, 0)
    }, 500)
  }, [step])

  return (
    <div className={classes.container}>
      {steps[step]}
    </div>
  )
}

Onboarding.propTypes = {
  onClose: PropTypes.func.isRequired,
}

export default Onboarding
